import * as T from 'three';
import {Matchmaking,matchConfig,type HostedRoom,type MatchError} from './matchmaking';
import {backoffMs,playerMessage} from 'portal-stack/client/snx-match.js';
import { Environment } from './environment';
import { Simulation } from './simulation';
import { Monkey } from './character';
import { Input } from './input';
import { JungleAudio } from './audio';
import { Activities, MODES, sampleGhost } from './activities';
import { Troop } from './npc';
import { regionAt } from './editor-model';
import { TraversalGuide } from './traversal-guide';
import { clearFraction } from './camera-occlusion';
import { atmosphere, setCoverage, setHorizon } from './stylize';
import { WaterEffects } from './water-effects';
import { Editor, escapeHTML as esc } from './editor';
import { loadProgress, saveProgress, validateMap } from './storage';
import { distance, type WorldData, type SimState, type Vec3 } from './types';
const $ = <E extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as E;
const progress = loadProgress();
try {
    if (matchMedia('(pointer:coarse)').matches && !localStorage.getItem('canopy.progress.v1'))
        progress.settings.quality = 'low';
}
catch { }
let playing = false, menu = true, journalTab = 'adventures', toastTimer = 0, startedOnce = false;
const canvas = $<HTMLCanvasElement>('game');
const renderer = new T.WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, progress.settings.quality === 'low' ? 1 : 1.6));
renderer.setSize(innerWidth, innerHeight);
renderer.shadowMap.enabled = progress.settings.quality !== 'low';
renderer.shadowMap.type = T.PCFSoftShadowMap;
renderer.toneMapping = T.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.13;
renderer.outputColorSpace = T.SRGBColorSpace;
// See-through fade: MSAA alpha-to-coverage when the drawing buffer is multisampled (soft
// sub-pixel translucency), noise-threshold discard otherwise and on the Light preset.
const msaa = (renderer.getContext().getParameter(renderer.getContext().SAMPLES) as number) > 0;
setCoverage(msaa && progress.settings.quality !== 'low');
const scene = new T.Scene();
// Atmospheric depth (Bible §3): distance cools toward a soft sky blue; the warm sun keeps the
// foreground golden. Fog, sky horizon and far-scenery haze share one colour and one range.
const horizon = new T.Color('#b4d8e4');
scene.background = horizon.clone();
scene.fog = new T.Fog(horizon, 30, 420);
setHorizon(horizon);
atmosphere.hazeRange.value.set(30, 420);
const camera = new T.PerspectiveCamera(60, innerWidth / innerHeight, .08, 850);
camera.position.set(12, 20, -16);
// Late-morning key: warm sun, sky-blue fill from above, warm bounce from below, so shadow masses
// read teal rather than grey (Bible §3).
scene.add(new T.HemisphereLight('#d6ecf4', '#7d6b45', 2.3));
const sun = new T.DirectionalLight('#ffe8bf', 2.8);
sun.position.set(-35, 80, -35);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.left = -36;
sun.shadow.camera.right = 36;
sun.shadow.camera.top = 36;
sun.shadow.camera.bottom = -36;
sun.shadow.camera.near = 1;
sun.shadow.camera.far = 170;
sun.shadow.normalBias = .13;
sun.shadow.bias = -.00015;
scene.add(sun, sun.target);
const fill = new T.DirectionalLight('#d5eeee', .5);
fill.position.set(35, 15, 50);
scene.add(fill);
const audio = new JungleAudio(() => progress.settings.volume);
function notify(text: string, kind = '') {
    const toast = $('toast');
    toast.querySelector('div')!.textContent = text;
    toast.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('visible'), 4200);
    if (kind)
        audio.play(kind as 'fruit');
}
function save() {
    if (!saveProgress(progress))
        notify('Device storage is full. This session’s progress is still available.');
}
try {
    const [world, sim, firstMonkey] = await Promise.all([fetch('/world/brightwild.json').then(r => {
            if (!r.ok)
                throw Error('The jungle map could not be loaded.');
            return r.json() as Promise<WorldData>;
        }), Simulation.create(), Monkey.create(progress.settings.character)]);
    const issues = validateMap(world);
    if (issues.length)
        throw Error(issues[0]);
    $('loading-copy').textContent = 'Growing a jungle of possibilities…';
    let mapEdited = false;
    let environment = new Environment(scene, world);
    sim.load(world);
    Monkey.register(world);
    sim.spawn(0, world.spawn);
    let player = firstMonkey;
    scene.add(player.root);
    player.dress(progress.settings.accessory);
    let state = sim.read();
    let checkpoint: Vec3 = [...world.spawn];
    let yaw = 0;
    let time = 0;
    let last = performance.now();
    let accumulator = 0;
    let hudClock = 0;
    let collected = new Set(progress.fruit);
    let fps = 60;
    let frames = 0;
    let fpsClock = 0;
    let recoveryCount = 0;
    let lastRecover = -Infinity, frozenUntil = 0;
    let waterTime = 0;
    const guide = new TraversalGuide(sim, world, scene);
    const waterEffects = new WaterEffects(scene);
    const input = new Input(canvas, () => progress.settings);
    const troop = new Troop(sim, world);
    let troopReady = false;
    troop.load().then(() => {
        troop.monkeys.forEach(m => scene.add(m.root));
        troopReady = true;
        activities.followPoints = troop.followPoints;
        for (const demo of troop.demos?.routes || []) {
            const route = world.routes.find(r => r.id === demo.id);
            if (route)
                Object.assign(route, { proven: true, physicsVersion: sim.version, initialState: demo.initialState, finalState: demo.states.at(-1), inputs: demo.inputs.map(a => ({ x: a[0], z: a[1], buttons: a[2], assist: a[3] })), trace: demo.states.filter((_, i) => i % 6 === 0) });
        }
        if(editor.active)editor.refreshView();
    }).catch(e => notify(`The troop is taking a little longer: ${e.message}`));
    let ghost: Monkey | undefined;
    Monkey.create('default', true).then(m => { ghost = m; scene.add(m.root); m.root.visible = false; });
    const activities = new Activities(world, progress, scene, notify, (name, elapsed, medal) => { playing = false; input.enabled = false; document.exitPointerLock(); audio.play('finish'); save(); $('result-title').textContent = name; $('result-medal').textContent = medal; $('result-time').textContent = `${elapsed.toFixed(2)}s`; $<HTMLDialogElement>('result').showModal(); });
    const editor = new Editor(world, sim, scene, () => { mapEdited = true; environment.rebuildTraversal(); }, notify, p => { camera.position.set(p[0] + 5, p[1] + 4, p[2] - 7); camera.lookAt(...p); });
    editor.attachView(camera, canvas);
    // A soft directional beacon guides objectives without turning the jungle into a wall of icons.
    const beacon = new T.Mesh(new T.OctahedronGeometry(.23), new T.MeshBasicMaterial({ color: '#ffe09a' }));
    scene.add(beacon);
    const carried = new T.Mesh(new T.SphereGeometry(.20, 10, 7), new T.MeshStandardMaterial({ color: '#e2ad49', roughness: .8 }));
    scene.add(carried);
    function resume() {
        menu = false;
        playing = true;
        input.enabled = true;
        input.reset();
        $('menu').hidden = true;
        $('hud').hidden = false;
        $<HTMLDialogElement>('journal').close();
        $<HTMLDialogElement>('result').close();
        audio.start().catch(() => { });
        if (!startedOnce) {
            startedOnce = true;
            activities.start('explore');
        }
        $('play-button').innerHTML = 'Back to the branches <span>↗</span>';
    }
    function pause() { playing = false; input.enabled = false; input.reset(); document.exitPointerLock(); menu = true; $('menu').hidden = false; $('hud').hidden = true; }
    function startMode(mode: string) {
        const roomMatch = ROOM_MATCHES[mode] ?? 0;
        if (network.connected && roomMatch && !network.isHost) {
            notify('Only the room host can start a shared match.');
            return;
        }
        if (troopReady)
            troop.reset();
        if (editor.active) {
            editor.toggle(state, input.sample());
            document.body.classList.remove('editor-open');
        }
        sim.spawn(0, world.spawn);
        checkpoint = [...world.spawn];
        state = sim.read();
        if (network.connected && network.isHost && (roomMatch || network.matchMode))
            network.command(`M ${roomMatch}`);
        activities.start(network.connected && mode in ROOM_MATCHES ? 'explore' : mode);
        input.yaw = 0;
        camera.position.set(state.p[0], state.p[1] + 4, state.p[2] - 8);
        resume();
    }
    function recover() {
        const now = performance.now();
        if (network.connected) {
            if (network.frozen) {
                notify(network.matchMode === 5 ? 'You’re jailed! A teammate can free you with a touch.' : 'You’re frozen! A friend can thaw you with a touch.');
                return;
            }
            // Mirror the room's return cooldown (3 s, 6 s in Tag) so a rejected return never snaps us locally.
            const cooldown = network.isTag ? 6000 : 3000;
            if (now - lastRecover < cooldown) {
                notify('Catch your breath — you can return to your perch in a moment.');
                return;
            }
            network.command('R');
            if (network.isTag)
                frozenUntil = now + 2500;
        }
        lastRecover = now;
        const old = sim.read();
        sim.spawn(0, checkpoint);
        state = sim.read();
        state.raw[19] = old.grabs;
        state.raw[20] = old.tails;
        recoveryCount = Math.max(recoveryCount, old.falls) + 1;
        state.raw[21] = recoveryCount;
        state.raw[23] = old.bestChain;
        sim.restore(state.raw);
        waterTime = 0;
        input.reset();
        if (activities.carrying)
            activities.drop(old);
        notify(old.mode === 4 ? 'Back on your last safe perch. Ready for another swing.' : 'Back on your last safe perch.');
    }
    function toggleEditor() {
        roomAttempt++;setJoining(false);
        if (network.connected) {
            network.disconnect();
            notify('Left the shared room to edit your local map.');
        }
        if ($<HTMLDialogElement>('journal').open)
            $<HTMLDialogElement>('journal').close();
        if (menu)
            resume();
        document.exitPointerLock();
        editor.toggle(state, input.sample());
        document.body.classList.toggle('editor-open', editor.active);
        input.enabled = !editor.active;
        input.reset();
        if (editor.active)
            notify('Frozen in flight. Place a branch, rewind, and test the catch.');
    }
    function openJournal(tab = 'adventures') { playing = false; input.enabled = false; input.reset(); document.exitPointerLock(); journalTab = tab; renderJournal(); $<HTMLDialogElement>('journal').showModal(); }
    function renderJournal() {
        document.querySelectorAll<HTMLElement>('[data-tab]').forEach(b => { const on = b.dataset.tab === journalTab; b.classList.toggle('active', on); b.setAttribute('aria-pressed', String(on)); });
        const content = $('journal-content');
        if (journalTab === 'adventures') {
            content.innerHTML = `<p class="section-intro">A jungle is better with a little mischief. Choose an adventure, or make your own.</p><div class="adventure-grid">${MODES.map(m => `<button class="adventure" data-mode="${m.id}"><span>${m.icon}</span><strong>${m.name}</strong><p>${m.text}</p><small>${progress.completed.includes(m.id) ? '✓ Completed · ' : ''}${progress.bests[`${m.id}:${progress.settings.assist}:v${sim.version}`] ? `Personal best ${progress.bests[`${m.id}:${progress.settings.assist}:v${sim.version}`].toFixed(2)}s` : 'LET’S GO ↗'}</small></button>`).join('')}</div>`;
            content.querySelectorAll<HTMLElement>('[data-mode]').forEach(b => b.onclick = () => startMode(b.dataset.mode!));
        }
        if (journalTab === 'map') {
            const minX = -95, maxX = 95, minZ = -20, maxZ = 155;
            const xy = (p: number[]) => [(p[0] - minX) / (maxX - minX) * 550, 380 - (p[2] - minZ) / (maxZ - minZ) * 350];
            content.innerHTML = `<p class="section-intro">${progress.discoveries.length} discoveries · ${progress.fruit.length} fruit collected. Return to a discovered landmark at any time.</p><div class="world-map"><svg viewBox="0 0 550 400" aria-label="Map of the nine Brightwild regions"><defs><pattern id="map-dots" width="18" height="18" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="1" fill="#b6c4a3" opacity=".5"/></pattern></defs><rect width="550" height="400" fill="url(#map-dots)"/>${world.routes.filter(r => r.points.length > 7).map(r => `<polyline points="${r.points.map(xy).map(p => p.join(',')).join(' ')}" fill="none" stroke="#b1bf98" stroke-dasharray="3 5" stroke-width="2"/>`).join('')}${world.regions.map(r => { const [x, y] = xy(r.center); return `<g><circle cx="${x}" cy="${y}" r="22" fill="${esc(r.color)}" opacity=".45"/><circle cx="${x}" cy="${y}" r="4" fill="#567950"/><text x="${x}" y="${y + 32}" text-anchor="middle" fill="#415b3f" font-size="13" font-family="sans-serif">${esc(r.name)}</text></g>`; }).join('')}<circle cx="${xy(state.p)[0]}" cy="${xy(state.p)[1]}" r="5" fill="#c38d3e" stroke="#fff5d8" stroke-width="2"/></svg><div class="poi-list">${world.pois.map(p => `<button data-travel="${esc(p.id)}" ${progress.discoveries.includes(p.id) ? '' : 'disabled'}>${progress.discoveries.includes(p.id) ? '✧' : '◌'} ${esc(p.name)}<small>${progress.discoveries.includes(p.id) ? 'Return to this landmark' : 'Waiting to be discovered'}</small></button>`).join('')}</div></div>`;
            content.querySelectorAll<HTMLElement>('[data-travel]').forEach(b => b.onclick = () => {
                const p = world.pois.find(p => p.id === b.dataset.travel)!;
                if (!progress.discoveries.includes(p.id))
                    return;
                if (activities.mode !== 'explore' && !activities.complete) {
                    notify('Finish or leave your adventure before fast travel.');
                    return;
                }
                sim.spawn(0, [p.position[0], p.position[1], p.position[2]]);
                checkpoint = [p.position[0], p.position[1], p.position[2]];
                resume();
            });
        }
        if (journalTab === 'troop') {
            const names = ['default', 'dark', 'golden', 'snow', 'red-faced', 'baby'], colors = ['#855733', '#302c26', '#c58a47', '#ede3c9', '#834534', '#b28758'];
            content.innerHTML = `<p class="section-intro">Six curious personalities. The same freedom to move. Choose the one that feels like you.</p><div class="monkey-grid">${names.map((n, i) => `<button type="button" data-monkey="${n}" aria-pressed="${progress.settings.character === n}" class="monkey-choice ${progress.settings.character === n ? 'active' : ''}" style="--fur:${colors[i]}"><i></i>${n === 'default' ? 'Original' : n === 'red-faced' ? 'Red-faced' : n[0].toUpperCase() + n.slice(1)}</button>`).join('')}</div><p class="section-intro" style="margin-top:28px">A little something for the journey.</p><div class="cosmetics">${['none', 'scarf', 'hat', 'flowers'].map(n => `<button type="button" data-accessory="${n}" aria-pressed="${progress.settings.accessory === n}" class="${progress.settings.accessory === n ? 'active' : ''}">${n === 'none' ? 'Just me' : n === 'scarf' ? 'Coral scarf' : n === 'hat' ? 'Explorer hat' : 'Wildflower crown'}</button>`).join('')}</div>`;
            content.querySelectorAll<HTMLElement>('[data-monkey]').forEach(b => b.onclick = async () => {
                b.textContent = 'Joining…';
                try {
                    const next = await Monkey.create(b.dataset.monkey!);
                    scene.remove(player.root);
                    player = next;
                    scene.add(player.root);
                    progress.settings.character = b.dataset.monkey!;
                    player.dress(progress.settings.accessory);
                    save();
                    renderJournal();
                }
                catch {
                    notify('That monkey could not be loaded. Please try again.');
                }
            });
            content.querySelectorAll<HTMLElement>('[data-accessory]').forEach(b => b.onclick = () => { progress.settings.accessory = b.dataset.accessory!; player.dress(progress.settings.accessory); save(); renderJournal(); });
        }
        if (journalTab === 'settings') {
            content.innerHTML = `<p class="section-intro">Find your rhythm. Assistance affects reach, never the joy of moving. Records are kept separately for each preset.</p><div class="settings-grid"><label>Helping hand<select id="setting-assist"><option value="0">Assisted · a little extra reach</option><option value="1">Standard · the natural flow</option><option value="2">Precision · every catch is yours</option></select><small>Hold grab to jump and catch. Release to fly, then hold again for the next branch. Tail reaches farther.</small></label><label>Jungle detail<select id="setting-quality"><option value="high">Lush · full foliage & shadows</option><option value="low">Light · fewer distant trees, no shadows</option></select><small>Choose Light for smaller devices or a smoother journey.</small></label><label>Jungle sounds<input id="setting-volume" type="range" min="0" max="1" step=".05"><small>Synthesized birds, wind, splashes and musical discoveries.</small></label><label>Camera sensitivity<input id="setting-sensitivity" type="range" min=".3" max="2" step=".1"><small>Mouse or right stick to look. Escape releases the mouse.</small></label><label><span><input id="setting-invert" type="checkbox"> Invert camera vertical movement</span></label><label><span><input id="setting-motion" type="checkbox"> Reduce camera motion</span></label></div><p class="section-intro" style="margin-top:25px">Keyboard: WASD move · Space jump and grab · Shift tail · Ctrl tuck · E carry / pass · Q drop · R return · F1 editor. Gamepad: left stick move, right stick look, hold RT to jump / catch, release to fly, LT tail, A jump, B tuck, Y return.</p>`;
            const s = progress.settings;
            for (const key of ['assist', 'quality', 'volume', 'sensitivity'] as const) {
                const el = $<HTMLInputElement>(`setting-${key}`);
                el.value = String(s[key]);
                el.oninput = () => {
                    if (key === 'quality') {
                        s.quality = el.value;
                        renderer.shadowMap.enabled = s.quality !== 'low';
                        setCoverage(msaa && s.quality !== 'low');
                        renderer.setPixelRatio(Math.min(devicePixelRatio, s.quality === 'low' ? 1 : 1.6));
                    }
                    else
                        s[key] = Number(el.value);
                    save();
                };
            }
            for (const [name, key] of [['invert', 'invertY'], ['motion', 'reducedMotion']] as const) {
                const el = $<HTMLInputElement>(`setting-${name}`);
                el.checked = s[key];
                el.onchange = () => { s[key] = el.checked; save(); };
            }
        }
        if (journalTab === 'together') {
            const localAddress=location.protocol==='https:'?'wss://'+location.host+'/room':'ws://'+(location.hostname.endsWith('.localhost')?location.host+'/room':location.hostname+':8800');
            content.innerHTML = `<p class="section-intro">A jungle is better together. Explore, play Monkey Tag (classic, Infection, Freeze, Team or Crown) or chase mangoes with up to 16 monkeys.</p><div class="settings-grid"><label>Your explorer name<input type="text" id="room-name" value="${esc(explorerName)}" maxlength="20"></label>${matchmaker?`<label>Adventure<select id="room-mode"><option value="explore">Explore together</option><option value="tag">Monkey Tag</option><option value="infection">Infection Tag</option><option value="freeze">Freeze Tag</option><option value="team">Team Tag</option><option value="crown">Crown Tag</option><option value="bandits">Banana Bandits</option></select></label>`:`<label>Room server<input type="text" id="room-server" value="${esc(localAddress)}" maxlength="180"></label>`}</div>${matchmaker?`<div class="cosmetics"><button id="room-quickplay" class="primary">Find a troop ↗</button><button id="room-private">Make a private room</button></div><div class="room-invite"><label>Friend’s room code<input id="room-code" maxlength="6" placeholder="ABC123" autocomplete="off" value="${esc(new URLSearchParams(location.search).get('room')||'')}"></label><button id="room-join">Join friend</button></div>${matchmaker.previous&&!network.connected?'<button id="room-resume">Return to your last room</button>':''}`:`<div class="cosmetics"><button id="room-connect" class="primary">Join the troop ↗</button></div>`}<div id="room-sharing" ${network.connected?'':'hidden'}><span>Your room code</span><strong id="room-share-code">${esc(hostedRoom?.code||network.roomCode||'Local room')}</strong><button id="room-copy">Copy invitation</button></div>${network.connected?`<div class="settings-grid"><label>Room match<select id="room-match">${Object.entries(ROOM_MATCH_NAMES).map(([id,name])=>`<option value="${id}" ${ROOM_MATCHES[id]===network.matchMode?'selected':''}>${name}</option>`).join('')}</select></label></div><div class="cosmetics"><button id="room-start" ${network.isHost?'':'disabled'}>Start match</button></div>${network.isHost?'':'<p class="section-intro">The room host starts and ends matches.</p>'}`:''}<button id="room-disconnect">Leave room</button><p id="room-status" class="section-intro" role="status" style="margin-top:20px">${esc(networkStatus)}</p>`;
            const action=(kind:'quickplay'|'private'|'join'|'resume'|'local')=>runRoomAction(kind);
            if(matchmaker){$('room-quickplay').onclick=()=>action('quickplay');$('room-private').onclick=()=>action('private');$('room-join').onclick=()=>action('join');document.getElementById('room-resume')?.addEventListener('click',()=>action('resume'));}
            else $('room-connect').onclick=()=>action('local');
            document.getElementById('room-start')?.addEventListener('click',()=>startMode($<HTMLSelectElement>('room-match').value));
            $('room-disconnect').onclick=()=>{roomAttempt++;joining=false;network.disconnect();hostedRoom=undefined;matchmaker?.forget();networkStatus='You left the troop. Solo play is ready.';renderJournal();};
            $('room-copy').onclick=async()=>{const code=hostedRoom?.code||network.roomCode;if(!code)return;const url=new URL(location.href);url.search='';url.searchParams.set('room',code);try{await navigator.clipboard.writeText(url.href);notify('Invitation copied. Share it with your troop!');}catch{notify(`Your room code is ${code}.`);}};
            setJoining(joining);
        }
    }
    const matchmaker=matchConfig?new Matchmaking(matchConfig):null;
    if(matchmaker)matchmaker.retry.onWaiting=w=>{networkStatus=w.message;const el=document.getElementById('room-status');if(el)el.textContent=w.message;};
    let hostedRoom:HostedRoom|undefined,joining=false,roomAttempt=0,explorerName='Explorer';
    function setJoining(value:boolean){joining=value;for(const id of ['room-quickplay','room-private','room-join','room-resume','room-connect']){const button=document.getElementById(id) as HTMLButtonElement|null;if(button)button.disabled=value;}}
    async function runRoomAction(kind:'quickplay'|'private'|'join'|'resume'|'local'){
        if(joining)return;
        if(mapEdited){notify('Export your map, then reload the original jungle to meet your troop.');return;}
        if(editor.active)toggleEditor();
        const attempt=++roomAttempt;setJoining(true);
        explorerName=$<HTMLInputElement>('room-name').value||'Explorer';
        const code=(document.getElementById('room-code') as HTMLInputElement|null)?.value||'';
        const mode=(document.getElementById('room-mode') as HTMLSelectElement|null)?.value||'explore';
        networkStatus='Finding a branch for your troop…';$('room-status').textContent=networkStatus;
        try{
            let url:string;
            if(kind==='local')url=$<HTMLInputElement>('room-server').value;
            else {const room=kind==='quickplay'?await matchmaker!.quickplay(mode):kind==='private'?await matchmaker!.createPrivate(mode):kind==='join'?await matchmaker!.join(code):await matchmaker!.resume();if(!room)throw Error('Your last room has closed. Find a new troop.');if(attempt!==roomAttempt)return;hostedRoom=room;url=matchmaker!.socketUrl(room);}
            if(attempt!==roomAttempt)return;
            network.disconnect();
            // A room server that refuses the upgrade (room full, at its room limit, draining) answers 503, which a
            // browser socket only sees as a failed connection: quickplay asks for another room instead of retrying it.
            for(let tries=1;;tries++){
                try{await network.connect(url,explorerName);break;}
                catch(error){if(kind!=='quickplay'||tries>=3||attempt!==roomAttempt)throw error;}
                await new Promise(resolve=>setTimeout(resolve,500*tries*(.5+Math.random()*.5)));
                if(attempt!==roomAttempt)return;
                const room=await matchmaker!.quickplay(mode);if(attempt!==roomAttempt)return;hostedRoom=room;url=matchmaker!.socketUrl(room);
            }
            if(attempt!==roomAttempt){network.disconnect();return;}
            activities.start('explore');resume();notify(hostedRoom?.code?`Welcome to the troop! Your room code is ${hostedRoom.code}.`:'Welcome to the troop!');
        }catch(error){networkStatus=(error as Error).message;notify(networkStatus);const el=document.getElementById('room-status');if(el)el.textContent=networkStatus;}
        finally{if(attempt===roomAttempt)setJoining(false);}
    }

    let networkStatus = 'Not connected.';
    const { Network, ROOM_MATCHES, ROLE, TEAMS } = await import('./network');
    const ROOM_MATCH_NAMES: Record<string, string> = { explore: 'Explore together', tag: 'Monkey Tag', infection: 'Infection Tag · caught monkeys join the chase', freeze: 'Freeze Tag · friends thaw the frozen', team: 'Team Tag · two teams take turns hunting', crown: 'Crown Tag · steal the crown and keep it', bandits: 'Banana Bandits' };
    const network = new Network(sim, scene, async () => Monkey.create('default'), text => {
        networkStatus = text;
        const el = document.getElementById('room-status');
        if (el)
            el.textContent = text;
        notify(text);
    });
    network.onTerminalClose=()=>{roomAttempt++;setJoining(false);hostedRoom=undefined;matchmaker?.forget();};
    // Drops resume the held seat for the 180 s window (1013 busy: wait 5-8 s first); busy answers back off honouring
    // Retry-After; a terminal answer (404/409/410, match started/ended, seat expired) ends it with its reason.
    network.onUnexpectedClose=close=>{
        if(!matchmaker||!hostedRoom){notify('The room disconnected. Solo play is ready.');return;}
        const attempt=++roomAttempt,until=Date.now()+180000,first=close.retry?5000+Math.random()*3000:0;
        setJoining(true);notify(close.retry?playerMessage(close.reason,{retryInMs:first}):'The branch slipped away. Reconnecting to your troop…');
        void (async()=>{let why:string|null|undefined,retryAfterMs:number|undefined;for(let n=0;Date.now()<until&&attempt===roomAttempt;n++){
            await new Promise(resolve=>setTimeout(resolve,n===0&&first?first:backoffMs(n,{retryAfterMs,capMs:8000})));
            if(attempt!==roomAttempt)return;
            try{const room=await matchmaker.resume();if(!room)break;if(attempt!==roomAttempt)return;hostedRoom=room;await network.connect(matchmaker.socketUrl(room),explorerName);if(attempt===roomAttempt){setJoining(false);notify('Back with your troop.');}return;}catch(error){const e=error as MatchError;retryAfterMs=e.retryAfterMs;if(e.terminal||e.status===403){why=e.reason;break;}}
        }if(attempt===roomAttempt){setJoining(false);hostedRoom=undefined;matchmaker.forget();networkStatus=`${why?playerMessage(why):Date.now()>=until?playerMessage('resume_expired'):'That room is no longer available.'} Find a new troop from the journal.`;notify(networkStatus);}})();
    };
    document.querySelectorAll<HTMLElement>('[data-call]').forEach(b => b.onclick = () => { activities.call(Number(b.dataset.call)); audio.play('fruit'); });
    $('play-button').onclick = resume;
    $('return-button').onclick = recover;
    $('guidance-button').onclick = () => {
        progress.settings.guidance = progress.settings.guidance === false;
        save();
    };
    $('pause-button').onclick = pause;
    $('together-button').onclick = () => openJournal('together');
    $('activities-button').onclick = () => openJournal('adventures');
    $('settings-button').onclick = () => openJournal('troop');
    $('journal-button').onclick = () => openJournal('adventures');
    $('map-button').onclick = () => openJournal('map');
    $('minimap-button').onclick = () => openJournal('map');
    $('touch-interact').onclick = () => {
        if (network.connected && network.matchMode)
            network.command('E');
        else
            activities.interact(state);
    };
    document.querySelectorAll<HTMLElement>('[data-close]').forEach(b => b.onclick = () => {
        if (menu)
            $<HTMLDialogElement>('journal').close();
        else
            resume();
    });
    document.querySelectorAll<HTMLElement>('[data-tab]').forEach(b => b.onclick = () => { journalTab = b.dataset.tab!; renderJournal(); });
    $<HTMLDialogElement>('journal').addEventListener('cancel', e => {
        e.preventDefault();
        if (menu)
            $<HTMLDialogElement>('journal').close();
        else
            resume();
    });
    $('result-retry').onclick = () => activities.authoredId ? editor.onPlayActivity?.(activities.authoredId) : startMode(activities.mode);
    $('result-explore').onclick = () => startMode('explore');
    $<HTMLDialogElement>('result').addEventListener('cancel', e => { e.preventDefault(); activities.start('explore'); resume(); });
    editor.onPlayActivity = id => { const race=world.activities.find(a=>a.id===id);if(!race)return; if(editor.active)toggleEditor();checkpoint=[...(race.start||race.gates[0])];sim.spawn(0,checkpoint);state=sim.read();activities.start('trial',id);resume(); };
    $('editor-button').onclick = toggleEditor;
    $('editor-close').onclick = toggleEditor;
    $('editor-freeze').onclick = () => editor.freeze(input.sample());
    input.onAction = code => {
        if (code === 'F1') {
            toggleEditor();
            return;
        }
        if (code === 'Escape') {
            if (editor.active)
                toggleEditor();
            else if (!menu && !$<HTMLDialogElement>('journal').open && !$<HTMLDialogElement>('result').open)
                pause();
            return;
        }
        if (code === 'F3') {
            $('performance').hidden = !$('performance').hidden;
            return;
        }
        if ($<HTMLDialogElement>('journal').open || menu)
            return;
        if (editor.active) {
            if (code === 'Space') {
                if (input.keys.has('ShiftLeft')) {
                    editor.armApex();
                    notify('Will freeze at the next flight apex.');
                }
                else
                    editor.freeze(input.sample());
            }
            if (code === 'KeyR')
                editor.rewind();
            if (code === 'KeyZ' && input.keys.has('ControlLeft'))
                editor.undo();
            if (code === 'KeyY' && input.keys.has('ControlLeft'))
                editor.redo();
            if (code.startsWith('Digit') && Number(code.slice(-1)) <= 6)
                editor.place([1, 2, 3, 5, 0, 4][Number(code.slice(-1)) - 1]);
            return;
        }
        if (code === 'KeyJ')
            openJournal();
        if (code === 'KeyM')
            openJournal('map');
        if (code === 'KeyR')
            recover();
        if (code === 'KeyE') {
            if (network.connected && network.matchMode)
                network.command('E');
            else
                activities.interact(state);
        }
        if (code === 'KeyQ') {
            network.command('Q');
            activities.drop(state);
        }
        if (code.startsWith('Digit'))
            activities.call(Number(code.slice(-1)));
    };
    window.addEventListener('resize', () => { camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix(); renderer.setSize(innerWidth, innerHeight); });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden && playing && !editor.active)
            pause();
        last = performance.now();
        accumulator = 0;
    });
    // Room-match scoreboard: top scorers (and always this monkey), with team points in Team Tag.
    let scoreboardHTML = '';
    function updateScoreboard() {
        const mode = network.matchMode;
        let html = '';
        if (network.connected && mode > 0 && network.board.length && !editor.active) {
            const ranked = [...network.board].sort((a, b) => b.score - a.score || a.id - b.id), shown = ranked.slice(0, 6), own = ranked.find(s => s.own);
            if (own && !shown.includes(own))
                shown.push(own);
            const status = (role: number) => mode === 6 ? (role === ROLE.crowned ? 'crown' : '')
                : mode === 5 ? (role === ROLE.frozen ? 'jailed' : '')
                : mode === 4 ? (role === ROLE.chaser ? 'chaser' : role === ROLE.frozen ? 'frozen' : '')
                : mode === 3 ? (role === ROLE.chaser ? 'infected' : '')
                : mode === 1 && role === ROLE.chaser ? 'it' : '';
            const teams = mode === 5 ? `<div class="score-teams">${TEAMS.map((t, i) => `<span style="--team:${t.color}"><i aria-hidden="true"></i>${t.name} <b>${network.teamScores[i]}</b>${network.hunters === i && network.clock >= 0 ? '<small>hunting</small>' : ''}</span>`).join('')}</div>` : '';
            const rows = shown.map(s => {
                const tag = status(s.role), team = TEAMS[s.team];
                return `<li class="${s.own ? 'me' : ''}"${team ? ` style="--team:${team.color}"` : ''}><span class="score-name">${team ? `<i aria-hidden="true"></i><span class="sr-only">${team.name}: </span>` : ''}${esc(s.own ? `${s.name} (you)` : s.name)}</span>${tag ? `<small class="score-tag">${tag === 'crown' ? '<span aria-hidden="true">♛</span> crown' : tag}</small>` : ''}<b>${s.score}${mode === 6 ? '<small> s</small>' : ''}</b></li>`;
            }).join('');
            html = `<h3>${esc(network.matchTitle)} · scores</h3>${teams}<ol>${rows}</ol>`;
        }
        if (html === scoreboardHTML)
            return;
        scoreboardHTML = html;
        const board = $('scoreboard');
        board.innerHTML = html;
        board.hidden = !html;
        document.body.classList.toggle('has-scoreboard', !!html);
    }
    function updateHUD() {
        $('monkey-calls').hidden = activities.mode !== 'memory' || editor.active;
        const speed = Math.hypot(...state.v);
        $('speed').textContent = speed.toFixed(0);
        $('speed-bar').style.width = `${Math.min(100, speed / 24 * 100)}%`;
        $('movement-state').textContent = ['ON YOUR FEET', 'FREE AS THE WIND', 'HOLD ON, LITTLE ONE', 'A VERY USEFUL TAIL', 'TAKE A LITTLE DIP', 'UP YOU GO'][state.mode] || 'EXPLORING';
        $('chain').textContent = state.chain > 1 ? `${state.chain} catches · keep the story going` : 'A world above the ordinary.';
        $('fruit-count').textContent = String(collected.size);
        $('fruit-total').textContent = `/ ${world.fruit.length}`;
        $('mode-name').textContent = activities.mode === 'explore' ? 'A LITTLE CURIOSITY' : activities.title.toUpperCase();
        $('objective').textContent = network.connected && network.matchMode ? network.matchTitle : activities.mode === 'explore' ? 'The First Ravine' : activities.title;
        $('objective-detail').textContent = network.connected && network.matchMode ? network.matchObjective : activities.objective;
        $('objective-distance').textContent = activities.target ? `${Math.round(distance(state.p, activities.target))} m to your next stop` : '';
        updateScoreboard();
        const nearest = regionAt(world,state.p);
        $('region-name').textContent = nearest.name;
        const ctx = $<HTMLCanvasElement>('minimap').getContext('2d')!;
        ctx.clearRect(0, 0, 170, 170);
        ctx.fillStyle = '#214d3d';
        ctx.fillRect(0, 0, 170, 170);
        ctx.strokeStyle = '#9eb98735';
        for (let i = 0; i < 4; i++) {
            ctx.beginPath();
            ctx.arc(85, 85, 22 + i * 20, 0, Math.PI * 2);
            ctx.stroke();
        }
        for (const r of world.regions) {
            const x = 85 + (r.center[0] - state.p[0]) * 1.45, y = 85 - (r.center[2] - state.p[2]) * 1.45;
            ctx.fillStyle = r.color;
            ctx.globalAlpha = .65;
            ctx.beginPath();
            ctx.arc(x, y, 9, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1;
        ctx.save();
        ctx.translate(85, 85);
        ctx.rotate(-yaw);
        ctx.fillStyle = '#f6d980';
        ctx.beginPath();
        ctx.moveTo(0, -7);
        ctx.lineTo(5, 5);
        ctx.lineTo(-5, 5);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        if (activities.target) {
            ctx.fillStyle = '#f5cd75';
            ctx.beginPath();
            ctx.arc(85 + (activities.target[0] - state.p[0]) * 1.45, 85 - (activities.target[2] - state.p[2]) * 1.45, 4, 0, Math.PI * 2);
            ctx.fill();
        }
        $('performance').textContent = `${fps.toFixed(0)} FPS · ${(1000 / fps).toFixed(1)} ms · ${renderer.info.render.triangles.toLocaleString()} triangles · ${renderer.info.render.calls} draws · ${world.objects.length} physics objects · ${state.grabs} grabs · ${state.tails} tail catches · ${recoveryCount} returns`;
    }
    function updateTraversalHUD(command: ReturnType<Input['sample']>) {
        const swimming = state.mode === 4;
        const enabled = playing && !editor.active;
        const hints = progress.settings.guidance !== false;
        const held = !!(command.buttons & 3);
        const attached = state.grab >= 0;
        const handDevice = input.keys.has('Space') ? 'keyboard' : input.grab ? input.grabSource : input.device;
        const tailDevice = input.keys.has('ShiftLeft') || input.keys.has('ShiftRight') ? 'keyboard' : input.tail ? input.tailSource : input.device;
        const control = state.mode !== 0 && command.buttons & 2 ? (tailDevice === 'touch' ? 'Tail' : tailDevice === 'gamepad' ? 'LT' : tailDevice === 'mouse' ? 'right mouse' : 'Shift') : handDevice === 'touch' ? 'Grab' : handDevice === 'gamepad' ? 'RT' : handDevice === 'mouse' ? 'left mouse' : 'Space';
        const move = input.device === 'touch' ? 'Push the stick' : input.device === 'gamepad' ? 'Push the left stick' : 'Hold W';
        $('swing-coach').hidden = !enabled || !hints || swimming;
        $('water-recovery').hidden = !enabled || !swimming;
        $('guidance-button').setAttribute('aria-pressed', String(hints));
        $('guidance-button').textContent = innerWidth <= 760 ? (hints ? 'Guide ✓' : 'Guide') : hints ? 'Swing guide: on' : 'Swing guide: off';
        $('guidance-button').setAttribute('aria-label', hints ? 'Hide swing guide' : 'Show swing guide');
        const stage = attached ? 1 : state.mode === 1 && state.raw[26] >= 0 ? 2 : 0;
        document.querySelectorAll<HTMLElement>('[data-swing-step]').forEach((el, i) => {
            el.classList.toggle('active', i === stage);
            if (i === stage) el.setAttribute('aria-current', 'step'); else el.removeAttribute('aria-current');
        });
        let title = `Hold ${control} to jump & catch`, detail = `${move} toward a branch. Keep holding ${control}; your hand catches automatically when it’s close.`;
        if (attached) {
            title = guide.preview ? `Release ${control} now → hold again` : `${move} to build your swing`;
            detail = guide.preview ? `Keep moving toward the marked catch. Let go, then hold ${control} again while flying.` : `Keep ${control} held. Steer toward your next branch; release when you swing forward and up.`;
        } else if (state.mode === 1) {
            title = held ? 'Reaching… keep holding' : `Hold ${control} now to catch`;
            detail = `Steer toward a branch. You can hold early—your hand catches as soon as it’s in reach.`;
        } else if (state.mode === 5) {
            title = `Hold ${control} to climb`;
            detail = 'Steer toward the trunk, then jump toward a branch.';
        } else if (!guide.preview) {
            title = 'Move closer to a branch';
            detail = `${move} toward the marker, then hold ${control} to jump and catch.`;
        }
        if (activities.carrying || network.carrying) {
            title = 'Your hands are full';
            detail = input.device === 'touch' ? 'Use Tail to catch, or E to deliver / pass.' : input.device === 'gamepad' ? 'Use LT to catch with your tail, or X to deliver / pass.' : 'Use Shift to catch with your tail, or Q to put it down.';
        }
        if ($('coach-title').textContent !== title) $('coach-title').textContent = title;
        $('coach-detail').textContent = detail;
        $('swing-coach').classList.toggle('release-ready', attached && !!guide.preview);
        $('water-countdown').textContent = `Back to your last safe perch in ${Math.max(1, Math.ceil(4 - waterTime))}…`;
        $('return-button').textContent = `Return now${input.device === 'gamepad' ? ' · Y' : input.device === 'touch' ? '' : ' · R'}`;
        $('touch-grab').textContent = attached && state.mode === 2 ? 'Release\nto fly' : held && command.buttons & 1 ? 'Reaching…' : 'Hold\nto grab';
        $('touch-grab').classList.toggle('held', !!(command.buttons & 1));
        $('touch-tail').classList.toggle('held', !!(command.buttons & 2));
        $('controls-hint').hidden = input.device === 'gamepad' || input.device === 'touch';
    }
    let cameraReach = 5.2, swingSide = 1, catchPulse = 0, catchSeen = 0;
    function cameraFocus(p: T.Vector3) {
        // Shared see-through uniform: dither out whatever sits between the lens and the monkey.
        camera.updateMatrixWorld();
        atmosphere.focus.value.set(p.x, p.y + .35, p.z).applyMatrix4(camera.matrixWorldInverse);
        atmosphere.fade.value = editor.active ? 0 : 1;
        atmosphere.sway.value = progress.settings.reducedMotion ? .015 : .07;
        // Temporal see-through noise integrates into translucency; static under reduced motion.
        atmosphere.frame.value = progress.settings.reducedMotion ? 0 : (atmosphere.frame.value + 1) % 64;
        atmosphere.sunView.value.copy(sun.position).sub(sun.target.position).normalize().transformDirection(camera.matrixWorldInverse);
    }
    function cameraStep(dt: number) {
        const p = new T.Vector3(...state.p);
        if (menu) {
            const t = time * .035;
            const dest = p.clone().add(new T.Vector3(11 + Math.sin(t) * 2, 5.8, -14));
            camera.position.lerp(dest, 1 - Math.exp(-dt * 2));
            camera.lookAt(p.clone().add(new T.Vector3(-3, 1, 6)));
            cameraFocus(p);
            return;
        }
        if (editor.active && editor.frozen)
            return;
        // Close, over-the-shoulder framing so the monkey reads large (≈5.2 m, FOV 57°), pulling back
        // and rising with speed and toward a distant next catch so the destination stays in frame.
        const calm = progress.settings.reducedMotion, speed = Math.hypot(...state.v);
        const vel = new T.Vector3(...state.v);
        let target: T.Vector3 | null = null;
        if (guide.preview && guide.preview.id !== state.grab) target = new T.Vector3(...guide.preview.point);
        else if (state.grab < 0 && state.candidate >= 0) {
            const o = world.objects.find(o => o.id === state.candidate);
            if (o) target = new T.Line3(new T.Vector3(...o.a), new T.Vector3(...o.b)).closestPointToPoint(p, true, new T.Vector3());
        }
        const ahead = target ? target.clone().sub(p) : null, gap = ahead ? Math.min(16, ahead.length()) : 0;
        const look = p.clone().add(new T.Vector3(vel.x * .07, .9 + Math.max(-.6, Math.min(.7, vel.y * .05)), vel.z * .07));
        if (ahead && gap > .5) look.addScaledVector(ahead, Math.min(.35, 3.2 / gap) * (calm ? .6 : 1));
        const distance = 5.2 + (calm ? 0 : Math.min(1.5, speed * .06)) + Math.min(1.4, gap * .09);
        const forward = new T.Vector3(Math.sin(input.yaw), 0, Math.cos(input.yaw)), side = new T.Vector3(-forward.z, 0, forward.x);
        // Hanging: drift toward the swing plane's side so the arc and the hand contact read.
        let lateral = .5;
        if (state.grab >= 0 && !calm) {
            const swing = new T.Vector3(vel.x, 0, vel.z);
            if (swing.lengthSq() > .25) {
                const across = Math.abs(swing.normalize().dot(side));
                swingSide = T.MathUtils.lerp(swingSide, Math.sign(camera.position.clone().sub(p).dot(side) || 1), 1 - Math.exp(-dt * 1.5));
                lateral = .5 + .75 * (1 - across) * swingSide;
            }
        }
        const desired = p.clone().addScaledVector(forward, -distance).addScaledVector(side, lateral).add(new T.Vector3(0, 1.15 + Math.min(.9, speed * .035) + Math.min(.8, gap * .05) + input.pitch * 4, 0));
        look.addScaledVector(side, lateral * .45);
        // Occlusion avoidance against trunks, decks, bridge walkways and big boughs: pull in fast,
        // ease back out slowly, and never closer than a size where the monkey stays readable.
        const offset = desired.clone().sub(look), full = offset.length(), minimum = 1.9;
        const clear = clearFraction(environment.solids, look, desired);
        const wanted = clear < 1 ? Math.max(minimum, clear * full - .35) : full;
        cameraReach += (wanted - cameraReach) * (1 - Math.exp(-dt * (wanted < cameraReach ? 16 : 2.4)));
        const dest = look.clone().addScaledVector(offset.normalize(), Math.min(full, cameraReach));
        camera.position.lerp(dest, 1 - Math.exp(-dt * (calm ? 7 : 11)));
        // Hard guard after smoothing: the lens may never sit behind a solid occluder.
        const toCamera = camera.position.clone().sub(look), reach = toCamera.length();
        const guard = reach > minimum ? clearFraction(environment.solids, look, camera.position) : 1;
        if (guard < 1)
            camera.position.copy(look).addScaledVector(toCamera.normalize(), Math.max(minimum, guard * reach - .25));
        camera.lookAt(look);
        // A brief ~4° tighten on each catch sells the grab impact (off under reduced motion).
        if (state.grabs > catchSeen) catchPulse = 1;
        catchSeen = state.grabs;
        catchPulse = Math.max(0, catchPulse - dt * 2.2);
        const fov = calm ? 57 : 57 + Math.min(6, speed * .26) - 4 * Math.sin(Math.PI * Math.min(1, catchPulse)) * catchPulse;
        camera.fov = T.MathUtils.lerp(camera.fov, fov, 1 - Math.exp(-dt * (catchPulse > 0 ? 8 : 2)));
        camera.updateProjectionMatrix();
        cameraFocus(p);
    }
    function frame(now: number) {
        requestAnimationFrame(frame);
        const dt = Math.max(0, Math.min(.10, (now - last) / 1000));
        last = now;
        time += dt;
        frames++;
        fpsClock += dt;
        if (fpsClock > .75) {
            fps = frames / fpsClock;
            frames = 0;
            fpsClock = 0;
        }
        const active = playing && (!editor.active || !editor.frozen);
        input.enabled = active;
        const previousFrame = state;
        let command = input.sample();
        if (performance.now() < frozenUntil)
            command = { ...command, x: 0, z: 0, buttons: 0 };
        if (editor.active && input.keys.has('Space'))
            command.buttons &= ~4;
        if (active) {
            accumulator += dt * (editor.active ? editor.testSpeed : 1);
            let steps = 0;
            while (accumulator >= 1 / 120 && steps++ < 16) {
                if (activities.carrying || network.carrying)
                    command.buttons &= ~1;
                network.input(command);
                sim.tick();
                // A Freeze Tag statue holds still until the room thaws it.
                if (!network.frozen)
                    sim.step(0, command);
                state = sim.read();
                waterTime = state.mode === 4 ? waterTime + 1 / 120 : 0;
                if (troopReady)
                    troop.step(state, activities.mode);
                editor.remember(state, command);
                accumulator -= 1 / 120;
                if (editor.active && editor.frozen) {
                    accumulator = 0;
                    break;
                }
            }
            activities.update(dt, state, troop.states);
            if (state.autoTails > previousFrame.autoTails)
                notify('A close call. Your tail caught the branch!', 'grab');
            if (state.grabs > previousFrame.grabs)
                audio.play('grab');
            if (state.mode === 4 && previousFrame.mode !== 4)
                audio.play('splash');
            if (network.connected && state.falls > previousFrame.falls) {
                recoveryCount = state.falls;
                input.reset();
                if (activities.carrying) activities.drop(previousFrame);
                notify('Back on your last safe perch. Ready for another swing.');
            }
            if (!editor.active && !network.connected && (state.p[1] < -5 || Math.abs(state.p[0]) > 350 || Math.abs(state.p[2]) > 400 || waterTime >= 4))
                recover();
            if (state.mode === 0)
                for (const o of world.objects) {
                    if (o.type === 0 && o.radius >= 2 && state.support === o.id) {
                        checkpoint = [state.p[0], state.p[1] + .05, state.p[2]];
                        break;
                    }
                }
            for (const fruit of world.fruit)
                if (!collected.has(fruit.id) && distance(state.p, fruit.position) < 1.25) {
                    collected.add(fruit.id);
                    progress.fruit.push(fruit.id);
                    audio.play('fruit');
                    player.emote('triumph');
                    save();
                }
            for (const poi of world.pois)
                if (!progress.discoveries.includes(poi.id) && (poi.requires||[]).every(id=>progress.discoveries.includes(id)) && distance(state.p, poi.position) < poi.radius) {
                    progress.discoveries.push(poi.id);
                    notify(`${poi.name} · ${poi.description}`, 'discover');
                    save();
                }
            if (Math.hypot(state.v[0], state.v[2]) > .4) {
                const target = Math.atan2(state.v[0], state.v[2]);
                yaw += Math.atan2(Math.sin(target - yaw), Math.cos(target - yaw)) * Math.min(1, dt * 9);
            }
        }
        else {
            accumulator = 0;
            state = sim.read();
            if (network.connected)
                network.input({ x: 0, z: 0, buttons: 0, assist: progress.settings.assist });
        }
        player.mark(network.teamColor(), network.crowned());
        player.update(dt, state, menu ? Math.PI : yaw);
        player.root.position.add(network.correction);
        if (troopReady)
            troop.update(dt, state, activities.mode === 'rescue' && activities.carrying);
        network.update(dt);
        if (ghost) {
            const tape = progress.ghosts[activities.recordKey];
            ghost.root.visible = !!tape && ['trial', 'race'].includes(activities.mode) && !menu;
            if (ghost.root.visible) {
                const sample = sampleGhost(tape, activities.elapsed);
                if (sample)
                    ghost.update(dt, { ...state, ...sample, grab: -1 }, Math.atan2(sample.v[0], sample.v[2]));
            }
        }
        environment.update(dt, new T.Vector3(...state.p), collected, progress.settings.quality, [state, ...troop.states, ...network.states]);
        // Dead branches: creak/crack/snap/regrow from the shared sim state, with a crack cue on snaps.
        if (environment.dead.size && environment.setBreaks(sim.breaks()).length)
            audio.play('crack');
        environment.water.position.y = activities.waterLevel - .1;
        waterEffects.update(dt, [state, ...network.states], environment.water.position.y, active);
        carried.visible = (activities.carrying && activities.mode !== 'rescue') || network.carrying;
        carried.position.set(state.p[0] + Math.cos(yaw) * .48, state.p[1] + .1, state.p[2] - Math.sin(yaw) * .48);
        if (network.connected && network.matchMode === 2)
            activities.target = network.carrying ? [0, 15, 0] : [-32, 19, 32];
        else if (activities.mode === 'explore')
            activities.target = null;
        beacon.visible = !!activities.target && !menu;
        if (activities.target) {
            beacon.position.set(activities.target[0], activities.target[1] + 1.8 + Math.sin(time * 2) * .15, activities.target[2]);
            beacon.rotation.y = time;
            const projected = beacon.position.clone().project(camera), visible = projected.z < 1 && Math.abs(projected.x) < .95 && Math.abs(projected.y) < .85;
            const label = $('target-label');
            label.hidden = !visible || menu || editor.active;
            label.style.left = `${(projected.x * .5 + .5) * innerWidth}px`;
            label.style.top = `${(-projected.y * .5 + .5) * innerHeight}px`;
            label.firstChild!.textContent = activities.mode === 'follow' ? 'Pip’s next catch' : 'Your next stop';
        }
        else
            $('target-label').hidden = true;
        editor.update();
        cameraStep(dt);
        const aiming = { ...command };
        if (state.mode === 0 && Math.hypot(aiming.x, aiming.z) < .1) {
            aiming.x = Math.sin(input.yaw); aiming.z = Math.cos(input.yaw);
        }
        guide.update(dt, state, aiming, camera, active && !editor.active && progress.settings.guidance !== false && !activities.carrying && !network.carrying);
        updateTraversalHUD(command);
        sun.position.set(state.p[0] - 35, state.p[1] + 60, state.p[2] - 35);
        sun.target.position.set(...state.p);
        environment.sky.setSun(sun.position.clone().sub(sun.target.position));
        audio.update(time, Math.hypot(...state.v));
        renderer.render(scene, camera);
        hudClock += dt;
        if (hudClock > .15) {
            hudClock = 0;
            updateHUD();
        }
    }
    $('loading').hidden = true;
    $('menu').hidden = false;
    document.body.dataset.ready = 'true';
    player.update(0, state, Math.PI);
    requestAnimationFrame(frame);
    // Read-only inspection supports browser QA and frame-budget measurement.
    Object.defineProperty(window, 'canopyGame', { value: { get ready() { return true; }, get state() { return structuredClone(state); }, get mode() { return activities.mode; }, get playing() { return playing; }, get editor() { return { active: editor.active, frozen: editor.frozen, objects: world.objects.length, history: editor.history.length, revision:editor.revision, selected:editor.selected, get document(){return structuredClone(world);} }; }, get metrics() { return { fps, triangles: renderer.info.render.triangles, draws: renderer.info.render.calls }; }, get progress() { return structuredClone(progress); }, get network() { return { connected: network.connected, remotes: network.count, roomCode:hostedRoom?.code||network.roomCode, managed:!!matchmaker }; }, get world() { return { regions: world.regions.length, objects: world.objects.length, pois: world.pois.length }; }, get pilots() { return troop.audit; }, get troop() { return troop.states.map(s => ({ p: s.p, mode: s.mode })); } }, writable: false });
    if (new URLSearchParams(location.search).get('play') === '1')
        resume();
}
catch (error) {
    console.error(error);
    $('loading-copy').textContent = `The jungle could not start: ${(error as Error).message}. Reload to try again.`;
}
